import { IContract } from '@monetize/types/app';
import { useState } from 'react';
import { IContractWithBillGroup } from '../../hooks/AccountDetail';
import { useContractActions } from '../../routes/Quotes/Quote/ContractActions';
import { MButton, MHStack, MTooltip } from '../Monetize';
import DataTableActions from '../Monetize/DataTable/MDataTableActions';

const OVERFLOW_BUTTON_PROPS = { 'aria-label': 'More contract actions' };

interface ContractActionButtonsProps {
  contract: IContract;
  handleEarlyRenewal?: (contract: IContractWithBillGroup) => void;
}

/**
 * Amend and Renew are always visible; when unavailable they are disabled with
 * a tooltip explaining why. Destructive actions (turn off auto renew, cancel)
 * stay behind an overflow menu and only appear when available.
 */
export const ContractActionButtons = ({
  contract,
  handleEarlyRenewal,
}: ContractActionButtonsProps) => {
  const { amend, renew, turnOffAutoRenew, cancel } = useContractActions({
    contract,
    handleEarlyRenewal,
  });
  const [pendingAction, setPendingAction] = useState<'amend' | 'renew'>();

  // Both actions navigate away on success; only reset if they return
  const runAction = async (key: 'amend' | 'renew', action: () => unknown) => {
    setPendingAction(key);
    try {
      await action();
    } finally {
      setPendingAction(undefined);
    }
  };

  const overflowActions = [turnOffAutoRenew, cancel];
  const hasOverflowActions = overflowActions.some(({ enabled }) => enabled);

  return (
    <MHStack gap={2}>
      <MTooltip
        label={amend.tooltipMsg}
        isDisabled={amend.enabled}
        shouldWrapChildren
      >
        <MButton
          variant="secondary"
          data-testid="contract-amend-btn"
          isLoading={pendingAction === 'amend'}
          isDisabled={!amend.enabled || !!pendingAction}
          onClick={() => runAction('amend', amend.action)}
        >
          Amend
        </MButton>
      </MTooltip>
      <MTooltip
        label={renew.tooltipMsg}
        isDisabled={renew.enabled}
        shouldWrapChildren
      >
        <MButton
          variant="primary"
          data-testid="contract-renew-btn"
          isLoading={pendingAction === 'renew'}
          isDisabled={!renew.enabled || !!pendingAction}
          onClick={() => runAction('renew', renew.action)}
        >
          Renew
        </MButton>
      </MTooltip>
      {hasOverflowActions && (
        <DataTableActions
          actions={overflowActions}
          variant="action"
          buttonProps={OVERFLOW_BUTTON_PROPS}
        />
      )}
    </MHStack>
  );
};
